import React, { useMemo } from 'react';

import {
  ActionIcon,
  Center,
  Checkbox,
  Divider,
  Grid,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconInfinity, IconTrash } from '@tabler/icons-react';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { computeEstimatedSessions } from 'utils/billing.helpers';

import EstimatedSessionsBadge from './EstimatedSessionsBadge';
import { PackageType } from '../../../types';
import { TimeBasedPackageFormValues } from '../types';

interface IAttendanceRuleCardProps {
  disabled: boolean;
  index: number;
  form: UseFormReturnType<
    TimeBasedPackageFormValues,
    (values: TimeBasedPackageFormValues) => TimeBasedPackageFormValues
  >;
  packageType: PackageType;
}

const TimeBasedAttendanceRuleCard = ({
  disabled,
  index,
  form,
  packageType,
}: IAttendanceRuleCardProps): JSX.Element => {
  const rule = form.values.attendanceRules[index];

  const estimatedSessions = useMemo(() => computeEstimatedSessions(rule, packageType), [rule, packageType]);

  const errorMessages = Object.entries(form.errors)
    .filter(error => error[0].includes(`attendanceRules.${index}`))
    .map(error => error[1]);

  const handleEventTypesChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    form.setFieldValue(
      `attendanceRules.${index}.kiloClassTypes`,
      values.filter(v => v.eventType === 'kilo_class_type'),
    );
    form.setFieldValue(
      `attendanceRules.${index}.appointmentTypes`,
      values.filter(v => v.eventType === 'appointment_type'),
    );
    const kiloClassTypeSet = new Set(
      values.filter(v => v.eventType === 'kilo_class_type').map(v => v.eventTypeId),
    );
    const appointmentTypeSet = new Set(
      values.filter(v => v.eventType === 'appointment_type').map(v => v.eventTypeId),
    );
    const oldKiloClassTypeSet = form.values.attendanceRules[index].oldKiloClassTypeSet;
    const oldAppointmentTypeSet = form.values.attendanceRules[index].oldAppointmentTypeSet;
    // @ts-ignore
    const classTypeIsSuperset = kiloClassTypeSet.isSupersetOf(oldKiloClassTypeSet);
    // @ts-ignore
    const appointmentTypeIsSuperset = appointmentTypeSet.isSupersetOf(oldAppointmentTypeSet);
    const appNotChanged =
      appointmentTypeIsSuperset && appointmentTypeSet.size === oldAppointmentTypeSet.size;
    const kiloNotChanged = classTypeIsSuperset && kiloClassTypeSet.size === oldKiloClassTypeSet.size;
    if (appNotChanged && kiloNotChanged) {
      form.setFieldValue(`attendanceRules.${index}.eventTypeState`, 'same');
    } else if (classTypeIsSuperset && appointmentTypeIsSuperset) {
      form.setFieldValue(`attendanceRules.${index}.eventTypeState`, 'superset');
    } else {
      form.setFieldValue(`attendanceRules.${index}.eventTypeState`, 'incompatible');
    }
  };

  const handleInfiniteSessions = (): void => {
    if (form.values.attendanceRules[index].credits !== null) {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        credits: null,
        interval: null,
        intervalCount: null,
        perOrPack: 'pack',
        isInfiniteSessions: true,
      });
    } else {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        credits: 1,
        interval: form.values.attendanceRules[index].validityInterval,
        intervalCount: 1,
        perOrPack: 'per',
        isInfiniteSessions: false,
      });
    }
  };

  const handlePerOrPackChange = (event: 'pack' | 'per'): void => {
    if (event === 'pack') {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        perOrPack: 'pack',
        interval: null,
        intervalCount: null,
      });
    } else if (event === 'per') {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        perOrPack: 'per',
        interval: form.values.attendanceRules[index].validityInterval,
        intervalCount: 1,
      });
    }
  };

  const handleDelayStartChange = (event: React.FormEvent<HTMLInputElement>): void => {
    const delayStart = event.currentTarget.checked;
    if (delayStart) {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        delayInterval: form.values.attendanceRules[index].validityInterval,
        delayIntervalCount: 1,
        delayStart,
      });
    } else {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        delayInterval: null,
        delayIntervalCount: null,
        delayStart,
      });
    }
  };

  return (
    <Paper shadow='sm' withBorder radius='md' bg={disabled ? 'gray.1' : undefined}>
      <Group position='apart' bg='gray.2' p='xs'>
        {disabled ? (
          <Text weight='bold' size='sm' sx={{ flex: 1 }}>
            {form.values.attendanceRules[index].name}
          </Text>
        ) : (
          <EventTypeMultiSelectAutoComplete
            size='xs'
            sx={{ flex: 1 }}
            placeholder='Select Event Type'
            values={[
              ...form.values.attendanceRules[index].kiloClassTypes,
              ...form.values.attendanceRules[index].appointmentTypes,
            ]}
            handleChange={handleEventTypesChange}
          />
        )}
        <ActionIcon onClick={() => form.removeListItem('attendanceRules', index)} disabled={disabled}>
          <IconTrash size='1.2rem' />
        </ActionIcon>
      </Group>

      <Grid p={0} m={0} gutter={0} columns={14}>
        <Grid.Col span={8} sx={{ borderRight: '1px solid #d3d3d3' }}>
          <Stack spacing='xs' p='sm'>
            <Input.Label required>Attendance</Input.Label>
            <Grid align='center' justify='center' gutter='xs' columns={29}>
              {!disabled && (
                <Grid.Col span={3}>
                  <ActionIcon
                    data-cy='infiniteSessionsButton'
                    color='kiloBlue'
                    variant={form.values.attendanceRules[index].isInfiniteSessions ? 'light' : 'outline'}
                    onClick={handleInfiniteSessions}
                    disabled={disabled}
                  >
                    <IconInfinity size='1rem' />
                  </ActionIcon>
                </Grid.Col>
              )}
              <Grid.Col span={5}>
                {form.values.attendanceRules[index].credits === null ? (
                  <TextInput value='&infin;' disabled size='xs' />
                ) : (
                  <NumberInput
                    size='xs'
                    min={1}
                    disabled={disabled}
                    {...form.getInputProps(`attendanceRules.${index}.credits`)}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={5}>
                <Text size='xs'>Sessions</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Select
                  {...form.getInputProps(`attendanceRules.${index}.perOrPack`)}
                  onChange={handlePerOrPackChange}
                  disabled={disabled || form.values.attendanceRules[index].isInfiniteSessions === true}
                  size='xs'
                  data-cy='perOrPackSelect'
                  data={[
                    { value: 'per', label: 'per', 'data-cy': 'perOption' },
                    { value: 'pack', label: 'pack', 'data-cy': 'packOption' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={5}>
                {form.values.attendanceRules[index].perOrPack === 'per' && (
                  <NumberInput
                    size='xs'
                    min={1}
                    disabled={disabled || form.values.allowProration === 'On'}
                    {...form.getInputProps(`attendanceRules.${index}.intervalCount`)}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={4}>
                {form.values.attendanceRules[index].perOrPack === 'per' && (
                  <Text size='xs'>{form.values.attendanceRules[index].interval}</Text>
                )}
              </Grid.Col>
            </Grid>
          </Stack>
        </Grid.Col>
        <Grid.Col span={3} sx={{ borderRight: '1px solid #d3d3d3' }}>
          <Stack spacing='xs' p='sm'>
            <Input.Label required>Validity</Input.Label>
            <Group grow noWrap>
              <NumberInput
                data-cy='packageValidityTime'
                min={1}
                size='xs'
                disabled={
                  disabled ||
                  (form.values.allowProration === 'On' &&
                    form.values.attendanceRules[index].perOrPack === 'pack')
                }
                max={365}
                {...form.getInputProps(`attendanceRules.${index}.validityIntervalCount`)}
                error={null}
              />
              <Text size='xs'>{form.values.attendanceRules[index].validityInterval}</Text>
            </Group>
          </Stack>
        </Grid.Col>
        <Grid.Col span={3}>
          <Stack spacing='xs' p='sm'>
            <Checkbox
              {...form.getInputProps(`attendanceRules.${index}.delayStart`)}
              checked={form.values.attendanceRules[index].delayStart}
              label='Delay Start'
              onChange={handleDelayStartChange}
              size='sm'
              data-cy='delayStartCheckBox'
              disabled={
                disabled ||
                form.values.allowProration === 'On' ||
                form.values.autoRenew === 'On' ||
                form.values.setSpecificStartDate === 'On'
              }
            />
            <Group grow noWrap>
              <NumberInput
                {...form.getInputProps(`attendanceRules.${index}.delayIntervalCount`)}
                value={form.values.attendanceRules[index].delayIntervalCount || 0}
                size='xs'
                min={1}
                disabled={disabled || !form.values.attendanceRules[index].delayStart}
              />
              <Text size='xs'>{form.values.attendanceRules[index].delayInterval}</Text>
            </Group>
          </Stack>
        </Grid.Col>
      </Grid>
      {errorMessages.length > 0 && (
        <>
          <Divider />
          <Stack py='sm' align='center'>
            {errorMessages.map(msg => (
              <Center key={`msg-${msg}`}>
                <Text size='xs' c='red'>
                  {msg}
                </Text>
              </Center>
            ))}
          </Stack>
        </>
      )}
      <EstimatedSessionsBadge result={estimatedSessions} />
    </Paper>
  );
};

export default TimeBasedAttendanceRuleCard;
